import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, Activity, ChevronDown, ChevronRight, Play, DollarSign, BarChart3, Target, Package, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useApi } from '../hooks/useApi';

const CombinedModels = () => {
  const { fetchPost } = useApi();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate dates: 2 weeks back for start_date, tomorrow for end_date
  const getDefaultDates = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(tomorrow)
    };
  };

  const defaultDates = getDefaultDates();

  const [formData, setFormData] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    fix_i_model_spread: 5,
    base_volume: 30,
    base_fix_i_model: 'v2.0.0',
    fix_i_model: 'v2.0.0',
    hourly_volume: 50,
    fix_ii_model: 'v1.0.0-fix2',
    buy_fix_i_margin: 300,
    sell_fix_i_margin: 300,
    fix_ii_margin: 1000,
    minimal_hourly_spread: 0,
    base_action_both_models_agree: false,
    preds_diff_cutoff: 5
  });


  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [, setShowHourlyPerformance] = useState(false);
  const [hourlyPerformanceData, setHourlyPerformanceData] = useState([]);

  // Automatically calculate hourly performance when results are available
  useEffect(() => {
    if (!results || !results.results) {
      return;
    }

    const hourlyPnL = {};
    for (let i = 0; i < 24; i++) {
      hourlyPnL[i] = 0;
    }

    Object.values(results.results).forEach(dayData => {
      if (dayData.hourly_results && Array.isArray(dayData.hourly_results)) {
        dayData.hourly_results.forEach(hourData => {
          if (hourData.pnl !== null && hourData.pnl !== undefined) {
            hourlyPnL[hourData.hour] += hourData.pnl;
          }
        });
      }
    });

    const chartData = Object.entries(hourlyPnL).map(([hour, pnl]) => ({
      hour: displayHours1to24 ? parseInt(hour) + 1 : parseInt(hour),
      pnl: parseFloat(pnl.toFixed(2))
    }));

    setHourlyPerformanceData(chartData);
    setShowHourlyPerformance(true);
  }, [displayHours1to24, results]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const runCombinedAnalysis = async (overrideFormData = null) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setShowHourlyPerformance(false);
    setHourlyPerformanceData([]);
    
    try {
      const dataToUse = overrideFormData || formData;
      
      const formDataToSend = new FormData();
      Object.entries(dataToUse).forEach(([key, value]) => {
        // Don't send base_fix_i_model if it's empty/null
        if (key === 'base_fix_i_model' && (value === '' || value === null || value === undefined)) {
          // Skip this field
        } else {
          formDataToSend.append(key, value);
        }
      });

      const response = await fetchPost('/combined-models', formDataToSend);

      const data = await response.json();
      
      if (data.status === 'success') {
        setResults(data);
      } else if (data.detail) {
        // Handle validation errors
        setError('Validation error: ' + JSON.stringify(data.detail));
      } else {
        setError(data.message || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running combined analysis:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (date) => {
    setExpandedRows(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const calculateDailyTradingSummary = (data) => {
    let summary = {
      base: {
        fix1: { buy: 0, sell: 0 },
        fix2: { buy: 0, sell: 0 }
      },
      hourly: {
        fix1: { buy: 0, sell: 0 },
        fix2: { buy: 0, sell: 0 }
      }
    };

    // Base trading calculations
    if (data.base_action_results && Array.isArray(data.base_action_results)) {
      data.base_action_results.forEach(hour => {
        // Fix I base actions
        if (hour.base_fix_i_action && hour.base_fix_i_volume > 0) {
          const value = hour.fix_i_price * hour.base_fix_i_volume;
          if (hour.base_fix_i_action === 'buy') {
            summary.base.fix1.buy += value;
          } else if (hour.base_fix_i_action === 'sell') {
            summary.base.fix1.sell += value;
          }
        }
        
        // Fix II base actions
        if (hour.base_fix_ii_action && hour.base_fix_ii_volume > 0) {
          const value = hour.fix_ii_price * hour.base_fix_ii_volume;
          if (hour.base_fix_ii_action === 'buy') {
            summary.base.fix2.buy += value;
          } else if (hour.base_fix_ii_action === 'sell') {
            summary.base.fix2.sell += value;
          }
        }
      });
    }

    // Hourly trading calculations
    if (data.hourly_results && Array.isArray(data.hourly_results)) {
      data.hourly_results.forEach(hour => {
        // Fix I hourly actions
        if (hour.fix_i_action && hour.fix_i_volume > 0) {
          const value = hour.fix_i_price * hour.fix_i_volume;
          if (hour.fix_i_action === 'buy') {
            summary.hourly.fix1.buy += value;
          } else if (hour.fix_i_action === 'sell') {
            summary.hourly.fix1.sell += value;
          }
        }
        
        // Fix II hourly actions
        if (hour.fix_ii_action && hour.fix_ii_volume > 0) {
          const value = hour.fix_ii_price * hour.fix_ii_volume;
          if (hour.fix_ii_action === 'buy') {
            summary.hourly.fix2.buy += value;
          } else if (hour.fix_ii_action === 'sell') {
            summary.hourly.fix2.sell += value;
          }
        }
      });
    }

    return summary;
  };

  const renderBaseActionsTable = (data, date) => {
    const baseActionData = data.base_action_results || [];

    if (!data.base_action || data.base_action === 'none' || baseActionData.length === 0) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
          <p className="text-gray-500 text-sm">No base actions taken</p>
        </div>
      );
    }

    const ordersData = baseActionData.filter(h => h.base_fix_i_action || h.base_fix_ii_action);
    if (ordersData.length === 0) return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <p className="text-gray-500 text-sm">No base orders executed</p>
      </div>
    );

    // Check if we should show quarterly Fix II display
    const dateObj = date ? new Date(date) : null;
    const useQuarterlyDisplay = dateObj && dateObj >= new Date('2025-10-01');

    // If quarterly display, expand data to include quarterly rows
    let expandedData = [];
    if (useQuarterlyDisplay) {
      ordersData.forEach(hour => {
        // Always add the main hour row
        expandedData.push({ ...hour, isQuarter: false });

        // If there's Fix II action and quarterly data, add quarterly rows
        if (hour.base_fix_ii_action && hour.base_fix_ii_quarterly) {
          for (let q = 1; q <= 4; q++) {
            const quarterData = hour.base_fix_ii_quarterly[`q${q}`];
            if (quarterData) {
              expandedData.push({
                ...hour,
                quarter: q,
                quarterTime: quarterData.time,
                isQuarter: true,
                quarterVolume: quarterData.volume || 0,
                quarterPnL: quarterData.pnl || 0,
                quarterPrice: quarterData.price,
                quarterOrderPrice: quarterData.order_price,
                quarterExecuted: quarterData.executed
              });
            }
          }
        }
      });
    } else {
      expandedData = ordersData.map(h => ({ ...h, isQuarter: false }));
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Base Actions {useQuarterlyDisplay && <span className="text-sm font-normal text-gray-600">(Fix II: 15-min intervals)</span>}
        </h3>
        <div className="mb-2 p-2 bg-gray-100 rounded">
          <span className="text-sm text-gray-700">
            Initial {data.base_action} of {data.base_volume} units at base price {formatNumber(data.base_price)} for all hours
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                {useQuarterlyDisplay && (
                  <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quarter</th>
                )}
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Pred</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Pred</th>
                <th colSpan="5" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I</th>
                <th colSpan="4" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">
                  Fix II {useQuarterlyDisplay && '(Quarterly)'}
                </th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
                <th rowSpan="2" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Residual</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Vol</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Executed</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expandedData.map((row, idx) => {
                const residual = row.base_residual || 0;

                // Determine background color
                let bgColor = '';
                if (row.isQuarter) {
                  bgColor = row.quarterPnL > 0 ? 'bg-green-50' : row.quarterPnL < 0 ? 'bg-red-50' : '';
                } else {
                  bgColor = row.base_pnl > 0 ? 'bg-green-50' : row.base_pnl < 0 ? 'bg-red-50' : '';
                }

                return (
                  <tr key={idx} className={bgColor}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {!row.isQuarter ? `${displayHours1to24 ? row.hour + 1 : row.hour}:00` : ''}
                    </td>
                    {useQuarterlyDisplay && (
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                        {row.isQuarter ? row.quarterTime : '-'}
                      </td>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {!row.isQuarter ? formatNumber(row.fix_i_prediction) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {!row.isQuarter ? formatNumber(row.fix_ii_prediction) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {!row.isQuarter && row.base_fix_i_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_i_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {!row.isQuarter ? formatNumber(row.base_fix_i_order_price) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {!row.isQuarter ? formatNumber(row.base_fix_i_volume || 0) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {!row.isQuarter ? formatNumber(row.fix_i_price) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {!row.isQuarter ? (
                        row.base_fix_i_volume > 0 ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-red-600">✗</span>
                        )
                      ) : ''}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {!row.isQuarter && row.base_fix_ii_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_ii_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_ii_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_ii_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {row.isQuarter ? formatNumber(row.quarterOrderPrice) : formatNumber(row.base_fix_ii_order_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {row.isQuarter ? formatNumber(row.quarterPrice) : formatNumber(row.fix_ii_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {row.isQuarter ? (
                        row.quarterVolume === 0 ? (
                          <span className="text-red-600 font-bold">✗</span>
                        ) : (
                          <span>
                            {formatNumber(row.quarterVolume)}
                            {row.quarterExecuted && <span className="ml-1 text-green-600">✓</span>}
                          </span>
                        )
                      ) : (
                        <span title={useQuarterlyDisplay && row.base_fix_ii_volume > 0 ?
                          `Executed across ${Math.round(row.base_fix_ii_volume / (data.base_volume / 4))} quarters` : ''}>
                          {formatNumber(row.base_fix_ii_volume || 0)}
                        </span>
                      )}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                      (row.isQuarter ? row.quarterPnL : row.base_pnl) > 0 ? 'text-green-600' :
                      (row.isQuarter ? row.quarterPnL : row.base_pnl) < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatNumber(row.isQuarter ? row.quarterPnL : row.base_pnl)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {!row.isQuarter && residual !== 0 && (
                        <span className="text-orange-600 font-medium">{residual}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderHourlyTradingTable = (hourlyData, date) => {
    if (!hourlyData || hourlyData.length === 0) return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Hourly Trading</h3>
        <p className="text-gray-500 text-sm">No hourly trading data</p>
      </div>
    );

    const ordersData = hourlyData.filter(h => h.fix_i_action || h.fix_ii_action);
    if (ordersData.length === 0) return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Hourly Trading</h3>
        <p className="text-gray-500 text-sm">No orders executed</p>
      </div>
    );

    // Check if we should show quarterly Fix II display
    const dateObj = date ? new Date(date) : null;
    const useQuarterlyDisplay = dateObj && dateObj >= new Date('2025-10-01');

    // For quarterly display, expand hourly data into quarterly rows
    if (useQuarterlyDisplay) {
      const expandedData = [];
      ordersData.forEach(hour => {
        // For Fix II quarterly display, create 4 rows per hour
        if (hour.fix_ii_action) {
          // Check if we have quarterly details from backend
          const hasQuarterlyDetails = hour.fix_ii_quarterly && Object.keys(hour.fix_ii_quarterly).length > 0;

          if (hasQuarterlyDetails) {
            // Use actual quarterly data from backend
            for (let q = 1; q <= 4; q++) {
              const quarterData = hour.fix_ii_quarterly ? hour.fix_ii_quarterly[`q${q}`] : null;

              if (quarterData) {
                expandedData.push({
                  ...hour,
                  quarter: q,
                  quarterTime: quarterData.time,
                  isQuarter: true,
                  quarterVolume: quarterData.volume || 0,
                  quarterPnL: quarterData.pnl || 0,
                  quarterPrice: quarterData.price,
                  quarterOrderPrice: quarterData.order_price,
                  quarterExecuted: quarterData.executed
                });
              } else {
                // Fallback if quarter data is missing
                const minutes = q * 15;
                let quarterTime;
                if (minutes === 60) {
                  if (hour.hour === 23) {
                    quarterTime = "24:00";
                  } else {
                    quarterTime = `${displayHours1to24 ? hour.hour + 2 : hour.hour + 1}:00`;
                  }
                } else {
                  quarterTime = `${displayHours1to24 ? hour.hour + 1 : hour.hour}:${minutes.toString().padStart(2, '0')}`;
                }

                expandedData.push({
                  ...hour,
                  quarter: q,
                  quarterTime: quarterTime,
                  isQuarter: true,
                  quarterVolume: 0,
                  quarterPnL: 0,
                  quarterPrice: null,
                  quarterOrderPrice: hour.fix_ii_order_price,
                  quarterExecuted: false
                });
              }
            }
          } else {
            // Fallback: divide evenly if no quarterly details
            for (let q = 1; q <= 4; q++) {
              const minutes = q * 15;
              let quarterTime;
              if (minutes === 60) {
                if (hour.hour === 23) {
                  quarterTime = "24:00";
                } else {
                  quarterTime = `${displayHours1to24 ? hour.hour + 2 : hour.hour + 1}:00`;
                }
              } else {
                quarterTime = `${displayHours1to24 ? hour.hour + 1 : hour.hour}:${minutes.toString().padStart(2, '0')}`;
              }

              expandedData.push({
                ...hour,
                quarter: q,
                quarterTime: quarterTime,
                isQuarter: true,
                quarterVolume: hour.fix_ii_volume ? hour.fix_ii_volume / 4 : 0,
                quarterPnL: hour.pnl ? hour.pnl / 4 : 0,
                quarterPrice: hour.fix_ii_price,
                quarterOrderPrice: hour.fix_ii_order_price,
                quarterExecuted: hour.fix_ii_volume > 0
              });
            }
          }
        } else {
          // If no Fix II action, just show the hourly row
          expandedData.push({...hour, isQuarter: false});
        }
      });

      // Build rows with headers repeated after every hour
      const tableRows = [];
      let currentHour = -1;

      expandedData.forEach((row, idx) => {
        // Insert header when starting a new hour (for quarterly display)
        if (row.isQuarter && row.quarter === 1 && currentHour !== row.hour) {
          currentHour = row.hour;
          // Add header row
          tableRows.push(
            <tr key={`header-${row.hour}`} className="bg-gray-50 border-t-2 border-gray-300">
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quarter</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Pred</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Pred</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I Action</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Order</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Vol</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Price</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Exec</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix II Action</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Order</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Price</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fix II Vol</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
            </tr>
          );
        }

        // Determine background color based on PnL
        let bgColor = '';
        if (row.isQuarter) {
          bgColor = row.quarterPnL > 0 ? 'bg-green-50' : row.quarterPnL < 0 ? 'bg-red-50' : '';
        } else {
          bgColor = row.pnl > 0 ? 'bg-green-50' : row.pnl < 0 ? 'bg-red-50' : '';
        }

        // Add data row
        tableRows.push(
          <tr key={`row-${idx}`} className={bgColor}>
            <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? (displayHours1to24 ? row.hour + 1 : row.hour) + ':00' : ''}
            </td>
            {useQuarterlyDisplay && (
              <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                {row.isQuarter ? row.quarterTime : '-'}
              </td>
            )}
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? formatNumber(row.fix_i_prediction) : ''}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? formatNumber(row.fix_ii_prediction) : ''}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
              {(row.quarter === 1 || !row.isQuarter) && row.fix_i_action && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  row.fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                  row.fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                  'bg-gray-200 text-gray-800'
                }`}>
                  {row.fix_i_action}
                </span>
              )}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? formatNumber(row.fix_i_order_price) : ''}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? formatNumber(row.fix_i_volume || 0) : ''}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {row.quarter === 1 || !row.isQuarter ? formatNumber(row.fix_i_price) : ''}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
              {(row.quarter === 1 || !row.isQuarter) && (
                row.fix_i_volume > 0 ? (
                  <span className="text-green-600">✓</span>
                ) : (
                  <span className="text-red-600">✗</span>
                )
              )}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
              {row.fix_ii_action && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  row.fix_ii_action === 'buy' ? 'bg-green-200 text-green-800' :
                  row.fix_ii_action === 'sell' ? 'bg-red-200 text-red-800' :
                  'bg-gray-200 text-gray-800'
                }`}>
                  {row.fix_ii_action}
                </span>
              )}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {formatNumber(row.isQuarter ? row.quarterOrderPrice : row.fix_ii_order_price)}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
              {formatNumber(row.isQuarter ? row.quarterPrice : row.fix_ii_price)}
            </td>
            <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
              {row.isQuarter ? (
                row.quarterVolume === 0 ? (
                  <span className="text-red-600 font-bold">✗</span>
                ) : (
                  <span>
                    {formatNumber(row.quarterVolume)}
                    {row.quarterExecuted && <span className="ml-1 text-green-600">✓</span>}
                  </span>
                )
              ) : (
                row.fix_ii_volume === 0 ? (
                  <span className="text-red-600 font-bold">✗</span>
                ) : (
                  formatNumber(row.fix_ii_volume)
                )
              )}
            </td>
            <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
              (row.isQuarter ? row.quarterPnL : row.pnl) > 0 ? 'text-green-600' :
              (row.isQuarter ? row.quarterPnL : row.pnl) < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {formatNumber(row.isQuarter ? row.quarterPnL : row.pnl)}
            </td>
          </tr>
        );
      });

      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Hourly Trading {useQuarterlyDisplay && <span className="text-sm font-normal text-gray-600">(Fix II: 15-min intervals)</span>}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="bg-white">
                {tableRows}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Original hourly display
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Hourly Trading</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
              <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix I Pred</th>
              <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fix II Pred</th>
              <th colSpan="5" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I</th>
              <th colSpan="4" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix II</th>
              <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
              <th rowSpan="2" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Residual</th>
            </tr>
            <tr>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Vol</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Executed</th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {ordersData.map((hour, idx) => {
              const residual = (hour.fix_i_volume || 0) - (hour.fix_ii_volume || 0);
              return (
                <tr key={idx} className={hour.pnl > 0 ? 'bg-green-50' : hour.pnl < 0 ? 'bg-red-50' : ''}>
                  <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {displayHours1to24 ? hour.hour + 1 : hour.hour}:00
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_i_prediction)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_ii_prediction)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                    {hour.fix_i_action && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        hour.fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                        hour.fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {hour.fix_i_action}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_i_order_price)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_i_volume || 0)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_i_price)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                    {hour.fix_i_volume > 0 ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-red-600">✗</span>
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                    {hour.fix_ii_action && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        hour.fix_ii_action === 'buy' ? 'bg-green-200 text-green-800' :
                        hour.fix_ii_action === 'sell' ? 'bg-red-200 text-red-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {hour.fix_ii_action}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_ii_order_price)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_ii_price)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(hour.fix_ii_volume || 0)}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                    hour.pnl > 0 ? 'text-green-600' : hour.pnl < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {formatNumber(hour.pnl)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                    {residual !== 0 && (
                      <span className="text-orange-600 font-medium">{residual}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDateRow = (date, data) => {
    const isExpanded = expandedRows[date];
    const totalPnl = data.daily_pnl || 0;
    const basePnl = data.base_pnl || 0;
    const fixingPnl = data.hourly_pnl || 0;
    const totalVolume = data.daily_total_volume_mwh || 0;
    const isPnLPositive = totalPnl > 0;
    const isPnLNegative = totalPnl < 0;

    // Count quarters with Fix II volume = 0 (for dates >= 2025-10-01)
    const dateObj = new Date(date);
    const useQuarterlyDisplay = dateObj >= new Date('2025-10-01');
    let zeroVolumeQuartersCount = 0;

    if (useQuarterlyDisplay && data.hourly_results) {
      data.hourly_results.forEach(hour => {
        // Check if there's Fix II action for this hour
        if (hour.fix_ii_action) {
          // If quarterly details exist, count quarters with zero volume
          if (hour.fix_ii_quarterly) {
            ['q1', 'q2', 'q3', 'q4'].forEach(quarter => {
              if (hour.fix_ii_quarterly[quarter] &&
                  hour.fix_ii_quarterly[quarter].volume === 0) {
                zeroVolumeQuartersCount++;
              }
            });
          } else if (hour.fix_ii_volume === 0) {
            // If no quarterly details but volume is 0, count all 4 quarters
            zeroVolumeQuartersCount += 4;
          }
        }
      });
    }

    return (
      <div key={date} className="border rounded-lg mb-4 overflow-hidden">
        <div
          className={`p-4 cursor-pointer transition-colors ${
            isPnLPositive ? 'bg-green-50 hover:bg-green-100' :
            isPnLNegative ? 'bg-red-50 hover:bg-red-100' :
            'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => toggleRow(date)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-semibold">{date}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.base_action === 'buy' ? 'bg-green-200 text-green-800' :
                data.base_action === 'sell' ? 'bg-red-200 text-red-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {data.base_action === 'none' ? 'idle' : data.base_action}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {Math.round(totalVolume)} MWh
              </span>
              {/* Display count of quarters with Fix II volume = 0 */}
              {useQuarterlyDisplay && zeroVolumeQuartersCount > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                  {zeroVolumeQuartersCount} Q
                </span>
              )}
            </div>
            <div className="flex flex-col space-y-1 text-sm">
              {/* First row: Base, Fix I Pred, Fix I, Fix II Pred, Fix II */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.base_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix I Pred:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_i_prediction)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix I:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_i_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix II Pred:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_ii_prediction)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix II:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_fix_ii_price)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Preds Diff:</span>
                  <span className={`ml-2 font-semibold ${
                    data.base_action_skipped_by_preds_diff || data.fixing_trades_skipped_by_preds_diff
                      ? (data.preds_diff > 0 ? 'text-red-600' : 'text-green-600')
                      : 'text-gray-900'
                  }`}>
                    {data.preds_diff !== null && data.preds_diff !== undefined
                      ? `${data.preds_diff > 0 ? '+' : ''}${data.preds_diff.toFixed(2)}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              {/* Second row: Base PnL, Fixing PnL, Daily PnL */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    basePnl > 0 ? 'text-green-600' : 
                    basePnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(basePnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Fixing PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    fixingPnl > 0 ? 'text-green-600' : 
                    fixingPnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(fixingPnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Daily PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    totalPnl > 0 ? 'text-green-600' : 
                    totalPnl < 0 ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {formatNumber(totalPnl)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 bg-white border-t">
            {renderBaseActionsTable(data, date)}
            {renderHourlyTradingTable(data.hourly_results, date)}
            
            {/* Trading Summary for this date */}
            {(() => {
              const tradingSummary = calculateDailyTradingSummary(data);
              const baseBuyTotal = tradingSummary.base.fix1.buy + tradingSummary.base.fix2.buy;
              const baseSellTotal = tradingSummary.base.fix1.sell + tradingSummary.base.fix2.sell;
              const hourlyBuyTotal = tradingSummary.hourly.fix1.buy + tradingSummary.hourly.fix2.buy;
              const hourlySellTotal = tradingSummary.hourly.fix1.sell + tradingSummary.hourly.fix2.sell;
              
              // Calculate base action value
              const baseActionValue = data.base_price * 24 * data.base_volume;
              let baseActionBuyValue = 0;
              let baseActionSellValue = 0;
              
              if (data.base_action === 'buy') {
                baseActionBuyValue = baseActionValue;
              } else if (data.base_action === 'sell') {
                baseActionSellValue = baseActionValue;
              }
              
              const totalBuyValue = baseBuyTotal + hourlyBuyTotal + baseActionBuyValue;
              const totalSellValue = baseSellTotal + hourlySellTotal + baseActionSellValue;
              const pnl = totalSellValue - totalBuyValue;

              return (
                <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>
                  
                  {/* Base Action Summary */}
                  {data.base_action && data.base_action !== 'none' && (
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-gray-700 mb-2">Base Action</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border rounded p-3 bg-white">
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {data.base_action === 'buy' ? 'Buy Value:' : 'Sell Value:'}
                              </span>
                              <span className={`font-semibold ${data.base_action === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                                {formatNumber(baseActionValue)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              ({formatNumber(data.base_price)} × 24h × {data.base_volume} MW)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Base Trading Summary */}
                  <div className="mb-4">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Base Trading</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix I</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.base.fix1.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.base.fix1.sell)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix II</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.base.fix2.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.base.fix2.sell)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Base Buy Value:</span>
                        <span className="font-bold text-green-700">{formatNumber(baseBuyTotal)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Base Sell Value:</span>
                        <span className="font-bold text-red-700">{formatNumber(baseSellTotal)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Hourly Trading Summary */}
                  <div className="mb-4">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Hourly Trading</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix I</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.hourly.fix1.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.hourly.fix1.sell)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-white">
                        <h5 className="font-medium text-gray-600 mb-2">Fix II</h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Buy Value:</span>
                            <span className="font-semibold text-green-600">{formatNumber(tradingSummary.hourly.fix2.buy)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sell Value:</span>
                            <span className="font-semibold text-red-600">{formatNumber(tradingSummary.hourly.fix2.sell)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Hourly Buy Value:</span>
                        <span className="font-bold text-green-700">{formatNumber(hourlyBuyTotal)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-700">Hourly Sell Value:</span>
                        <span className="font-bold text-red-700">{formatNumber(hourlySellTotal)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Final Totals */}
                  <div className="border-t pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex justify-between items-center bg-green-100 p-3 rounded">
                        <span className="font-medium text-gray-700">Total Buy Value:</span>
                        <span className="text-lg font-bold text-green-800">{formatNumber(totalBuyValue)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-red-100 p-3 rounded">
                        <span className="font-medium text-gray-700">Total Sell Value:</span>
                        <span className="text-lg font-bold text-red-800">{formatNumber(totalSellValue)}</span>
                      </div>
                      <div className={`flex justify-between items-center p-3 rounded ${
                        pnl > 0 ? 'bg-green-100' : pnl < 0 ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        <span className="font-medium text-gray-700">PnL:</span>
                        <span className={`text-lg font-bold ${
                          pnl > 0 ? 'text-green-800' : pnl < 0 ? 'text-red-800' : 'text-gray-800'
                        }`}>
                          {formatNumber(pnl)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Combined Models Analysis</h1>
          <p className="text-gray-600">Run combined analysis of Base Model and Fixing Model</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Fix I Model Spread</span>
              </label>
              <input
                type="number"
                value={formData.fix_i_model_spread}
                onChange={(e) => handleInputChange('fix_i_model_spread', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Base Volume</span>
              </label>
              <input
                type="number"
                value={formData.base_volume}
                onChange={(e) => handleInputChange('base_volume', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Base Fix I Model</span>
              </label>
              <select
                value={formData.base_fix_i_model || ''}
                onChange={(e) => handleInputChange('base_fix_i_model', e.target.value)}
                disabled={formData.base_action_both_models_agree}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formData.base_action_both_models_agree ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
              >
                <option value="">-- Not Required --</option>
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
              </select>
              {formData.base_action_both_models_agree && (
                <p className="text-xs text-gray-500 mt-1">
                  Disabled: Using Fix I and Fix II models agreement mode
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix I Model</span>
              </label>
              <select
                value={formData.fix_i_model}
                onChange={(e) => handleInputChange('fix_i_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Base action when both models agree</span>
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.base_action_both_models_agree}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    handleInputChange('base_action_both_models_agree', isChecked);
                    // Clear base_fix_i_model when checkbox is checked
                    if (isChecked) {
                      handleInputChange('base_fix_i_model', '');
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  {formData.base_action_both_models_agree ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, base action requires both Fix I and Fix II models to agree on direction (ignores Base Fix I model)
              </p>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <BarChart3 size={16} />
                <span>Fixing Volume</span>
              </label>
              <input
                type="number"
                value={formData.hourly_volume}
                onChange={(e) => handleInputChange('hourly_volume', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fix II Model</span>
              </label>
              <select
                value={formData.fix_ii_model}
                onChange={(e) => handleInputChange('fix_ii_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.0-fix2-cp14">v1.0.0-fix2-cp14</option>
                <option value="v1.0.0-fix2-exaa">v1.0.0-fix2-exaa</option>
                <option value="v2.0.0-fix2">v2.0.0-fix2</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                <option value="v2.0.0-narx-fix2">v2.0.0-narx-fix2</option>
                
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={16} />
                <span>Buy Fix I Margin</span>
              </label>
              <input
                type="number"
                value={formData.buy_fix_i_margin}
                onChange={(e) => handleInputChange('buy_fix_i_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={16} />
                <span>Sell Fix I Margin</span>
              </label>
              <input
                type="number"
                value={formData.sell_fix_i_margin}
                onChange={(e) => handleInputChange('sell_fix_i_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={16} />
                <span>Fix II Margin</span>
              </label>
              <input
                type="number"
                value={formData.fix_ii_margin}
                onChange={(e) => handleInputChange('fix_ii_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Filter size={16} />
                <span>Minimal Hourly Spread</span>
              </label>
              <input
                type="number"
                value={formData.minimal_hourly_spread}
                onChange={(e) => handleInputChange('minimal_hourly_spread', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.1"
                min="0"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Preds Diff Cutoff (%)</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.preds_diff_cutoff}
                onChange={(e) => handleInputChange('preds_diff_cutoff', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => runCombinedAnalysis()}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
              }`}
            >
              <Play size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
            </button>
            
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = formatDate(tomorrow);
                const updatedFormData = {
                  ...formData,
                  start_date: tomorrowStr,
                  end_date: tomorrowStr
                };
                setFormData(updatedFormData);
                // Run analysis with the updated data
                runCombinedAnalysis(updatedFormData);
              }}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-[1.02]'
              }`}
            >
              <Calendar size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis For Tomorrow'}</span>
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Summary</h2>
            
            {/* Key Metrics - First Row: Day Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Days</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {results.summary.total_days || 0}
                    </p>
                  </div>
                  <Calendar className="text-purple-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Profitable Days</p>
                    <p className="text-2xl font-bold text-green-800">
                      {results.summary.profitable_days || 0}
                    </p>
                    <p className="text-xs text-green-600">
                      {formatPercentage(results.summary.profitable_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingUp className="text-green-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Loss Days</p>
                    <p className="text-2xl font-bold text-red-800">
                      {results.summary.loss_days || 0}
                    </p>
                    <p className="text-xs text-red-600">
                      {formatPercentage(results.summary.loss_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingDown className="text-red-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Second Row: Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Buy Actions</p>
                    <p className="text-2xl font-bold text-emerald-800">
                      {results.summary.base_model_actions?.buy || 0}
                    </p>
                    <p className="text-xs text-emerald-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.buy)} of actions
                    </p>
                  </div>
                  <TrendingUp className="text-emerald-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-rose-600 font-medium">Sell Actions</p>
                    <p className="text-2xl font-bold text-rose-800">
                      {results.summary.base_model_actions?.sell || 0}
                    </p>
                    <p className="text-xs text-rose-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.sell)} of actions
                    </p>
                  </div>
                  <TrendingDown className="text-rose-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Idle Days</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.summary.base_model_actions?.none || 0}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.none)} of actions
                    </p>
                  </div>
                  <Activity className="text-gray-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Average Daily PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.avg_daily_combined_pnl >= 0 ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {formatNumber(results.summary.avg_daily_combined_pnl)}
                    </p>
                  </div>
                  <BarChart3 className="text-blue-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Third Row: PnL and Volume Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 1. Total Base PnL */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Base PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_base_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_base_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-blue-500" size={32} />
                </div>
              </div>

              {/* 2. Total Fixing PnL */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 font-medium">Total Fixing PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_fixing_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_fixing_pnl)}
                    </p>
                  </div>
                  <Activity className="text-indigo-500" size={32} />
                </div>
              </div>

              {/* 3. Total Combined PnL */}
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-cyan-600 font-medium">Total Combined PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_combined_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_combined_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-cyan-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fourth Row: Volume and Efficiency Part 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 4. Base Volume Traded */}
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-violet-600 font-medium">Base Volume Traded</p>
                    <p className="text-2xl font-bold text-violet-800">
                      {formatNumber(results.summary.total_base_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-violet-600">
                      BASE contract volume executed
                    </p>
                  </div>
                  <Package className="text-violet-500" size={32} />
                </div>
              </div>

              {/* 5. Profit per MWh Traded in BASE */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Profit/MWh in BASE</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_base >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_base || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-purple-600">
                      BASE trading efficiency
                    </p>
                  </div>
                  <Target className="text-purple-500" size={32} />
                </div>
              </div>

              {/* 6. Arbitrage Volume Traded */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-teal-600 font-medium">Arbitrage Volume Traded</p>
                    <p className="text-2xl font-bold text-teal-800">
                      {formatNumber(results.summary.total_arbitrage_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-teal-600">
                      Hourly trading volume executed
                    </p>
                  </div>
                  <Activity className="text-teal-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fifth Row: Volume and Efficiency Part 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* 7. Profit per MWh Traded in Arbitrage */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Profit/MWh in Arbitrage</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_arbitrage >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_arbitrage || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-emerald-600">
                      Arbitrage trading efficiency
                    </p>
                  </div>
                  <Target className="text-emerald-500" size={32} />
                </div>
              </div>

              {/* 8. Total Volume Traded */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Total Volume Traded</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {formatNumber(results.summary.total_volume_traded_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-amber-600">
                      Combined BASE + Arbitrage
                    </p>
                  </div>
                  <Package className="text-amber-500" size={32} />
                </div>
              </div>

              {/* 9. Profit per MWh Traded (Overall) */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Profit per MWh Traded</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-orange-600">
                      Overall trading efficiency
                    </p>
                  </div>
                  <Target className="text-orange-500" size={32} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Results */}
        {results && results.results && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Daily Results</h2>
            <div className="space-y-2">
              {Object.entries(results.results)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDateRow(date, data))}
            </div>
            
            {/* Hourly Performance Chart */}
            {hourlyPerformanceData.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Hourly Performance Analysis</h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="hour" 
                        label={{ value: 'Hour', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        label={{ value: 'PnL', angle: -90, position: 'insideLeft' }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip formatter={(value) => formatNumber(value)} />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="pnl">
                        {hourlyPerformanceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? "#10b981" : "#ef4444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Positive Hours</p>
                    <p className="text-xl font-bold text-green-700">
                      {hourlyPerformanceData.filter(d => d.pnl > 0).length}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Negative Hours</p>
                    <p className="text-xl font-bold text-red-700">
                      {hourlyPerformanceData.filter(d => d.pnl < 0).length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CombinedModels;
